import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ConfirmDialog } from "../../shared/ui/ConfirmDialog"
import {
  listNotificationsApi,
  deleteNotificationApi,
  NotificationCampaign,
} from "../../features/notifications/api/notifications.api"
import {
  MegaphoneIcon,
  UsersIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  TrashIcon,
} from "@heroicons/react/16/solid"

const LIMIT = 20

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })

const FilterChips = ({ filters }: { filters: NotificationCampaign["filters"] }) => {
  const chips: string[] = []
  if (filters.inactive_days) chips.push(`неактивен ${filters.inactive_days}д`)
  if (filters.role) chips.push(filters.role)
  if (filters.city) chips.push(filters.city)
  if (filters.industry) chips.push(filters.industry)
  if (chips.length === 0) return <span className="text-xs text-gray-300">Все пользователи</span>
  return (
    <div className="flex flex-wrap gap-1">
      {chips.map((c, i) => (
        <span key={i} className="text-[11px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-md capitalize">{c}</span>
      ))}
    </div>
  )
}

export const NotificationHistoryPage = () => {
  const navigate = useNavigate()
  const [items, setItems] = useState<NotificationCampaign[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)

  const [deleteTarget, setDeleteTarget] = useState<NotificationCampaign | null>(null)
  const [deleting, setDeleting] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError("")
    try {
      const offset = (page - 1) * LIMIT
      const res = await listNotificationsApi(LIMIT, offset)
      setItems(res.data.items)
      setHasMore(res.data.items.length === LIMIT)
    } catch {
      setError("Не удалось загрузить историю рассылок")
      setItems([])
      setHasMore(false)
    } finally {
      setLoading(false)
    }
  }, [page])

  useEffect(() => { load() }, [load])

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await deleteNotificationApi(deleteTarget.id)
      setItems((prev) => prev.filter((i) => i.id !== deleteTarget.id))
      setDeleteTarget(null)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="bg-white rounded-xl border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50/40">
              <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Кампания</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Сегмент</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Доставка</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Автор</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Дата</th>
              <th className="px-5 py-3 w-10" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 6 }).map((_, j) => (
                    <td key={j} className="px-5 py-4"><div className="h-4 bg-gray-100 rounded animate-pulse w-24" /></td>
                  ))}
                </tr>
              ))
            ) : error ? (
              <tr>
                <td colSpan={6} className="px-5 py-16 text-center">
                  <ExclamationTriangleIcon className="w-8 h-8 text-red-200 mx-auto mb-2" />
                  <p className="text-sm text-red-400">{error}</p>
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-5 py-16 text-center">
                  <MegaphoneIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">Рассылок пока не было</p>
                </td>
              </tr>
            ) : (
              items.map((n) => (
                <tr
                  key={n.id}
                  onClick={() => navigate(`/admin/notifications/history/${n.id}`)}
                  className="hover:bg-gray-50/60 transition-colors group cursor-pointer"
                >
                  <td className="px-5 py-3.5 max-w-[240px]">
                    <p className="font-medium text-gray-800 truncate">{n.title}</p>
                    <p className="text-xs text-gray-400 truncate">{n.body}</p>
                  </td>
                  <td className="px-5 py-3.5"><FilterChips filters={n.filters} /></td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3 text-xs">
                      <span className="inline-flex items-center gap-1 text-gray-500"><UsersIcon className="w-3.5 h-3.5 text-gray-300" />{n.target_count}</span>
                      <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircleIcon className="w-3.5 h-3.5" />{n.sent_count}</span>
                      {n.failed_count > 0 && (
                        <span className="inline-flex items-center gap-1 text-red-500"><ExclamationTriangleIcon className="w-3.5 h-3.5" />{n.failed_count}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-gray-600">{n.created_by_name}</td>
                  <td className="px-5 py-3.5 text-xs text-gray-400 whitespace-nowrap">{formatDate(n.created_at)}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(n) }}
                        title="Убрать из истории"
                        className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                      <ChevronRightIcon className="w-4 h-4 text-gray-200 group-hover:text-blue-400 transition-colors" />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!loading && (page > 1 || hasMore) && (
        <div className="flex items-center justify-between px-5 py-3.5 border-t bg-gray-50/40">
          <span className="text-xs text-gray-400">Страница <span className="font-medium text-gray-600">{page}</span></span>
          <div className="flex gap-1.5">
            <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              ← Назад
            </button>
            <button disabled={!hasMore} onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              Вперёд →
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Убрать из истории?"
        message={deleteTarget ? `Запись о рассылке «${deleteTarget.title}» будет удалена из истории. Саму рассылку это не отменяет — она уже отправлена.` : ""}
        confirmLabel="Убрать"
        loading={deleting}
        danger
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}
